<template>
  <v-card>
    <v-card-title class="d-flex align-center pa-4">
      <span class="text-button">Confirmar domicilio</span>
      <v-spacer></v-spacer>
      <v-tooltip text="Cerrar" location="left">
        <template v-slot:activator="{ props }">
          <v-btn
            v-bind="props"
            :icon="closeIcon"
            @click="closeModal"
            variant="text"
            size="small"
          >
          </v-btn>
        </template>
      </v-tooltip>
    </v-card-title>
    <v-card-text>
      <p class="text-subtitle-1 mb-3 mt-n6">
        Los medicamentos del tipo "{{tipoExpendioNombre.toLowerCase()}}" requieren del domicilio del paciente.
      </p>
      <div v-if="isLoading" class="d-flex align-center">
        <v-progress-circular indeterminate color="primary" class="mr-3" />
        <span>
          Buscando domicilio
          <template v-if="autoConsultarGecros && autoConsultarRenaper"></template>
          <template v-else-if="autoConsultarGecros"></template>
          <template v-else-if="autoConsultarRenaper">en RENAPER</template>
          <template v-else>datos</template>…
        </span>
      </div>
      <div v-else>
        <div v-if="domicilioEncontrado">
          <v-alert type="success" variant="tonal" class="mb-n4">
            Se encontró un domicilio {{ fuenteDomicilio === 'GECROS' ? 'del afiliado' : 'en RENAPER' }}. Por favor, confirme si es correcto.
          </v-alert>
          <v-list density="compact">
            <v-list-item>
              <v-list-item-title>{{ domicilioDisplay }}</v-list-item-title>
              <v-list-item-subtitle v-if="domicilio.CPostal">CP: {{ domicilio.CPostal }}</v-list-item-subtitle>
            </v-list-item>
          </v-list>
        </div>
        <div v-else>
          <v-alert type="warning" variant="tonal" class="mb-n4">
            No se ha encontrado domicilio. Verifique los datos del paciente.
          </v-alert>
        </div>
      </div>
    </v-card-text>
    <v-card-actions class="pt-0 mr-3">
      <v-spacer />
      <v-btn :size="isMobile ? 'small' : undefined" variant="text" color="primary" @click="onCancelar">Cancelar</v-btn>
      <v-btn :size="isMobile ? 'small' : undefined" variant="text" color="primary" @click="onOmitir">Omitir</v-btn>
      <v-btn v-if="domicilioEncontrado" :size="isMobile ? 'small' : undefined" color="primary" @click="onModificar">Modificar</v-btn>
      <v-btn v-if="domicilioEncontrado" :size="isMobile ? 'small' : undefined" color="primary" @click="onAceptar">Aceptar</v-btn>
      <v-btn v-else :size="isMobile ? 'small' : undefined" color="primary" @click="onModificar">Ingresar domicilio</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions } from 'vuex';
import enums from "@/utils/enums/index";

export default {
  name: 'ConfirmarDomicilioModal',
  props: {
    paciente: { type: Object, required: true }, // { nroDoc, cuil, tipoDoc, sexo }
    domicilioGecros: { type: Object, default: null }, // Domicilio GECROS del afiliado
    tipoExpendioNombre: { type: String, required: true },
    solicitudHash: { type: Number, required: false },
    autoConsultarRenaper: { type: Boolean, default: true },
    autoConsultarGecros: { type: Boolean, default: true },
    isMobile: { type: Boolean, default: false },
  },
  data() {
    return {
      closeIcon: enums.icons.CLOSE_ICON,
      isLoading: true,
      domicilio: {},
      domicilioEncontrado: false,
      renaperResponse: null,
      fuenteDomicilio: null, // 'GECROS' o 'RENAPER'
    };
  },
  computed: {
    domicilioDisplay() {
      const d = this.domicilio || {};
      if (d.DireccionCompleta) return d.DireccionCompleta.toUpperCase();

      // Construir la dirección con el formato específico
      let direccion = '';

      // Calle y número base
      if (d.Calle && d.Numero) {
        direccion = `${d.Calle} ${d.Numero}`;
      } else if (d.Calle) {
        direccion = d.Calle;
      } else if (d.Numero) {
        direccion = d.Numero;
      }

      // Agregar piso y departamento si existen
      const pisoDepto = [];
      if (d.Piso) pisoDepto.push(d.Piso);
      if (d.Departamento) pisoDepto.push(d.Departamento);

      if (pisoDepto.length > 0) {
        direccion += ` (${pisoDepto.join(' ')})`;
      }

      // Agregar municipio, ciudad, provincia y país (en ese orden específico)
      const partesAdicionales = [d.Municipio, d.Ciudad, d.Provincia, d.Pais].filter(Boolean);

      if (partesAdicionales.length > 0) {
        direccion += ', ' + partesAdicionales.join(', ');
      }

      return direccion.toUpperCase();
    },
  },
  async mounted() {
    await this.handleVisibleOpened();
  },
  methods: {
    isDomicilioVacio(domicilio) {
      if (!domicilio) return true;
      const valores = Object.values(domicilio || {});
      return !valores.some(v => v !== null && v !== undefined && (typeof v !== 'string' || v.trim() !== ''));
    },
    hasValidDomicilioData(domicilio) {
      return !this.isDomicilioVacio(domicilio);
    },
    mapDomicilioGecrosToDomicilio(domicilioGecros) {
      if (!domicilioGecros) return null;

      // Si paisId es 1, usar "Argentina" aunque paisNombre sea null
      const paisNombre = domicilioGecros.paisId === 1 ? "Argentina" : domicilioGecros.paisNombre;

      return {
        Calle: domicilioGecros.calle,
        Numero: domicilioGecros.numero,
        Piso: domicilioGecros.piso,
        Departamento: domicilioGecros.departamento,
        Barrio: domicilioGecros.barrioNombre,
        Provincia: domicilioGecros.provinciaNombre,
        CPostal: domicilioGecros.codigoPostal,
        Pais: paisNombre,
        Municipio: null, // Siempre null según usuario
        Ciudad: domicilioGecros.localidadNombre, // LocalidadNombre de Gecros ES Ciudad
        Monoblock: null, // No existe en Gecros
        Observaciones: null // No existe en Gecros
      };
    },
    async handleVisibleOpened() {
      this.isLoading = true;
      this.domicilioEncontrado = false;
      this.fuenteDomicilio = null;

      // CASO 1: Ambas banderas false - Solo modo manual
      if (!this.autoConsultarGecros && !this.autoConsultarRenaper) {
        this.domicilioEncontrado = false;
        this.isLoading = false;
        return;
      }

      // CASO 3: Solo GECROS habilitado
      if (this.autoConsultarGecros && !this.autoConsultarRenaper) {
        const hasValidGecrosData = this.domicilioGecros && this.hasValidDomicilioData(this.domicilioGecros);

        if (hasValidGecrosData) {
          this.domicilio = this.mapDomicilioGecrosToDomicilio(this.domicilioGecros);
          this.domicilioEncontrado = true;
          this.fuenteDomicilio = 'GECROS';
        }
        this.isLoading = false;
        return;
      }

      // CASO 2: Solo RENAPER habilitado
      if (!this.autoConsultarGecros && this.autoConsultarRenaper) {
        await this.buscarDomicilioRenaper();
        return;
      }

      // CASO 4: Ambas habilitadas - Prioridad GECROS > RENAPER
      if (this.autoConsultarGecros && this.autoConsultarRenaper) {
        const hasValidGecrosData = this.domicilioGecros && this.hasValidDomicilioData(this.domicilioGecros);

        if (hasValidGecrosData) {
          // Encontró en GECROS - usar y terminar
          this.domicilio = this.mapDomicilioGecrosToDomicilio(this.domicilioGecros);
          this.domicilioEncontrado = true;
          this.fuenteDomicilio = 'GECROS';
        } else {
          // No encontró en GECROS - buscar en RENAPER
          await this.buscarDomicilioRenaper();
        }
      }

      this.isLoading = false;
    },
    ...mapActions({
      consultarRenaper: 'solicitud/consultarRenaper',
      setAlert: 'user/setAlert'
    }),
    getDniFromCuil(cuil) {
      try {
        const digits = (cuil || '').toString().replace(/\D/g, '');
        if (digits.length >= 10) return digits.slice(2, 10);
        return null;
      } catch { return null; }
    },
    mapSexoToRenaperId(sexo) {
      if (!sexo) return 3; // Indeterminado
      const s = (sexo || '').toString().toUpperCase();
      if (s === 'F') return 1;
      if (s === 'M') return 2;
      return 3;
    },
    mapRenaperToDomicilioDTO(p) {
      if (!p) return {};

      // Solo usar DireccionCompleta si viene específicamente de RENAPER
      // Si se puede construir con campos individuales, dejar que el display use el formato estructurado
      const tieneCamposIndividuales = p.calle || p.numero || p.ciudad || p.provincia || p.pais;
      const direccionCompleta = p.direccionCompleta || p.direccion || (tieneCamposIndividuales ? null : null);

      return {
        Calle: p.calle || null,
        Numero: p.numero || null,
        Piso: p.piso || null,
        Departamento: p.departamento || null,
        Monoblock: p.monoblock || null,
        Barrio: p.barrio || null,
        Ciudad: p.ciudad || null,
        Municipio: p.municipio || null,
        Provincia: p.provincia || null,
        Pais: p.pais || null,
        CPostal: p.cpostal || null,
        Observaciones: null,
        DireccionCompleta: direccionCompleta || null
      };
    },
    async buscarDomicilioRenaper() {
      try {
        // Regla: priorizar CUIL; si no hay y es DNI, usar nroDoc; si NO es DNI, no consultar
        const tipoDoc = (this.paciente?.tipoDoc || '').toString().toUpperCase();
        const cuil = this.paciente?.cuil || null;
        const dniFromCuil = cuil ? this.getDniFromCuil(cuil) : null;
        let nroDocumento = null;
        if (dniFromCuil) nroDocumento = dniFromCuil;
        else if (tipoDoc === 'DNI') nroDocumento = (this.paciente?.nroDoc || '').toString().replace(/\D/g, '');
        else {
          // Pasaporte u otro tipo: no consultar
          this.domicilioEncontrado = false;
          return;
        }
        if (!nroDocumento) {
          this.domicilioEncontrado = false;
          return;
        }
        const idSexo = this.mapSexoToRenaperId(this.paciente?.sexo);
        const response = await this.consultarRenaper({ NroDocumento: nroDocumento, IdSexo: idSexo });
        if (response && response.status === 200 && response.data) {
          this.renaperResponse = response.data;
          this.domicilio = this.mapRenaperToDomicilioDTO(response.data);
          // Considerar encontrado si hay al menos un campo de dirección significativo
          this.domicilioEncontrado = Boolean(
            this.domicilio.Calle || this.domicilio.Ciudad || this.domicilio.Municipio || this.domicilio.Provincia
          );
          if (this.domicilioEncontrado) {
            this.fuenteDomicilio = 'RENAPER';
          }
        } else {
          this.domicilioEncontrado = false;
        }
      } catch (error) {
        console.error('Error consultando RENAPER', error);
        this.setAlert({ type: 'warning', message: 'No fue posible consultar RENAPER en este momento.' });
        this.domicilioEncontrado = false;
      } finally {
        this.isLoading = false;
      }
    },
    async onAceptar() {
      // Emitir null si el domicilio está vacío para dejar claro que no hay domicilio del paciente
      this.$emit('accept', this.isDomicilioVacio(this.domicilio) ? null : this.domicilio);
    },
    async onModificar() {
      // Emitir null si el domicilio está vacío
      this.$emit('modify', this.domicilioEncontrado ? (this.isDomicilioVacio(this.domicilio) ? null : this.domicilio) : null);
    },
    async onCancelar() {
      this.$emit('cancel');
    },
    async onOmitir() {
      this.$emit('omit', null);
    },
    closeModal() {
      this.$emit('cancel');
    },
  },
};
</script>

<style scoped>
</style>


